import dayjs from "dayjs";
import { parseLocalizedName } from "../../../utils/localization";
import { getStorageUrl } from "../../../shared/api/AllApi";
import { groupExpensesByType } from "./expenseTypes";
import { getPrintColors, readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { printHtml, openPrintWindow } from "../../../shared/lib/printHtml";
import { formatSettlementNo, formatLiveStatementNo } from "../../../shared/lib/documentNumbers";
import { formatCurrency, formatNumber } from "../../../utils/formatters";
import { vendorFinanceFromConsignment, vendorItemAmount } from "./vendorFinance";

const esc = (x) => String(x ?? "").replace(/[&<>"']/g, (c) => ({
  "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
}[c]));

const money = (v) => {
  const code = readCachedSystemConfig()?.currency_code || "IQD";
  return formatCurrency(v, code);
};

const printLang = (language = "en") => String(language || "en").split("-")[0] || "en";
const isRtlLang = (lang) => lang === "ku" || lang === "ar";

const expensePrintLabel = (t, code, fallback) => {
  if (typeof t === "function") {
    return t(`expenseType_${code}`, fallback || code);
  }
  return fallback || code;
};

/** Resolve invoice/sidebar logo to an absolute URL (or pass-through data/http). */
export function resolveLogoUrl(systemConfig) {
  const cfg = systemConfig || {};
  const raw = cfg.invoice_logo_url || cfg.sidebar_logo_url || cfg.invoice_logo || cfg.sidebar_logo || "";
  if (!raw) return "";
  const s = String(raw).trim();
  if (!s) return "";
  if (s.startsWith("data:") || s.startsWith("blob:") || s.startsWith("http://") || s.startsWith("https://")) {
    return s;
  }
  // Relative app asset (e.g. /static/media/…)
  if (s.startsWith("/") || s.startsWith("static/")) {
    try { return new URL(s.startsWith("/") ? s : `/${s}`, window.location.origin).href; } catch { return s; }
  }
  return getStorageUrl(s) || s;
}

/** Embed logo as data-URL so print iframes always show it (no CORS / late-load blank). */
export async function embedLogoForPrint(systemConfig) {
  const url = resolveLogoUrl(systemConfig);
  if (!url) return "";
  if (url.startsWith("data:")) return url;
  try {
    const res = await fetch(url, { credentials: "include", cache: "force-cache" });
    if (!res.ok) return url;
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || url));
      reader.onerror = () => reject(new Error("read failed"));
      reader.readAsDataURL(blob);
    });
  } catch {
    return url;
  }
}

/**
 * Shared print CSS: do not lock @page to A4 — printer can use A4 or A5.
 * Layout scales down on short/narrow pages (A5 portrait ~148×210, A5 landscape ~210×148).
 */
export const PRINT_ADAPTIVE_CSS = `
@page { margin: 7mm; }
html, body { width: 100%; }
.sheet { width: 100%; max-width: 100%; margin: 0 auto; }
img, .logo, .logo-ph { max-width: 100%; height: auto; }
table { max-width: 100%; }
@media print and (max-width: 190mm), print and (max-height: 180mm) {
  body { font-size: 13px !important; font-weight: 700 !important; }
  .logo, .logo-ph { width: 52px !important; height: 52px !important; line-height: 52px !important; }
  .company { font-size: 20px !important; font-weight: 900 !important; }
  .tagline { font-size: 13px !important; font-weight: 800 !important; }
  .title { font-size: 16px !important; font-weight: 900 !important; margin: 6px 0 8px !important; padding: 4px 0 !important; }
  .farmer-id, .for-block, .contact-box { font-size: 13px !important; font-weight: 800 !important; }
  .period, .meta, .acct, .phones, .managers, .top-meta { font-size: 12px !important; font-weight: 700 !important; }
  .header { grid-template-columns: 64px 1fr 64px !important; gap: 6px !important; }
  .mid { gap: 8px !important; padding-bottom: 6px !important; margin-bottom: 6px !important; }
  .summary-wrap { grid-template-columns: 1fr !important; gap: 8px !important; }
  .lrow { grid-template-columns: 78px 1fr !important; gap: 4px !important; margin-bottom: 4px !important; }
  .rrow { grid-template-columns: 1fr 86px !important; gap: 4px !important; margin-bottom: 4px !important; }
  .lbl { font-size: 12px !important; font-weight: 800 !important; }
  .box { font-size: 13px !important; font-weight: 800 !important; min-height: 24px !important; padding: 3px 6px !important; }
  .rrow.strong .box { font-size: 14px !important; font-weight: 900 !important; }
  .notes-block { padding: 6px 8px !important; margin-bottom: 6px !important; }
  .notes-block .notes-title { font-size: 13px !important; font-weight: 900 !important; margin-bottom: 4px !important; }
  .notes-block .note-debt { font-size: 13px !important; font-weight: 800 !important; padding: 2px 0 !important; }
  .notes-block .note-debt-sum { font-size: 14px !important; font-weight: 900 !important; margin-top: 4px !important; padding-top: 5px !important; }
  .detail th, .detail td { font-size: 12px !important; font-weight: 800 !important; padding: 2px 3px !important; }
  .items th, .items td { font-size: 12px !important; font-weight: 700 !important; padding: 4px 3px !important; }
  .meta td { font-size: 12px !important; font-weight: 700 !important; padding: 4px 5px !important; }
  .summary td { font-size: 13px !important; font-weight: 700 !important; padding: 4px 6px !important; }
  .summary .strong .lbl, .summary .strong .val { font-size: 14px !important; font-weight: 900 !important; }
  .notes { min-height: 56px !important; padding: 6px !important; }
  .footer { font-size: 11px !important; font-weight: 700 !important; flex-wrap: wrap !important; gap: 4px !important; }
}
`;

// Re-exported for backwards compatibility. The canonical, Safari-safe printer
// now lives in shared/lib/printHtml.
export { printHtml, openPrintWindow };

/**
 * Farmer settlement print — shows each expense type with its amount (not hardcoded buckets).
 */
export async function printFarmerSettlementStatement({
  consignment,
  vendor,
  systemConfig,
  language = "en",
  t = null,
  dateFrom = null,
  dateTo = null,
  settledAt = null,
  archiveId = null,
  expenseTypes = [],
  previousDebt = null,
  totalDebt = null,
  documentNo = null,
  note = null,
  printedBy = null,
  printWin: existingPrintWin = null,
}) {
  if (!consignment || !vendor) return;
  // Prefer a window opened on the user click (before any await) so Safari/iOS
  // print preview works. Otherwise open now (fine on Chrome/Edge).
  const printWin =
    existingPrintWin && !existingPrintWin.closed
      ? existingPrintWin
      : openPrintWindow();
  const tr = typeof t === "function" ? t : ((key, fallback) => {
    if (fallback && typeof fallback === "object" && fallback.defaultValue != null) return fallback.defaultValue;
    return typeof fallback === "string" ? fallback : key;
  });
  const lang = printLang(language);
  const rtl = isRtlLang(lang);
  const cfg = systemConfig || {};
  const colors = getPrintColors(cfg);
  const company = (cfg.invoice_company_name || "").trim() || cfg.site_name || "";
  const tagline = (cfg.invoice_tagline || "").trim();
  const phoneLines = (cfg.invoice_phones || "").trim()
    ? cfg.invoice_phones.split(/\r?\n/).map((s) => s.trim()).filter(Boolean)
    : [];
  const managersHtml = phoneLines.length
    ? phoneLines.map((p) => esc(p)).join("<br/>")
    : (vendor.phone ? esc(vendor.phone) : "");
  const logoSrc = await embedLogoForPrint(cfg);

  const s = consignment;
  const expenseRows = groupExpensesByType(s.expenses || [], expenseTypes).map((r) => ({
    ...r,
    label: expensePrintLabel(tr, r.code, r.label),
  }));
  const totalExp = (parseFloat(s.total_expenses) || 0)
    || expenseRows.reduce((a, r) => a + r.amount, 0);
  // Farmer slip: product gross − expenses only (never include commission).
  const finance = vendorFinanceFromConsignment({
    ...s,
    total_expenses: totalExp || s.total_expenses,
    total_commission: 0,
  });
  const gross = finance.gross;
  const net = finance.netOwed;

  const products = Array.isArray(s.products) ? s.products : [];
  let totalKg = products.reduce((a, p) => a + (parseFloat(p.kg_sold) || 0), 0);
  let totalCases = products.reduce((a, p) => a + (parseFloat(p.cases_sold) || 0), 0);

  const lineQty = (it) =>
    parseFloat(it.sold_qty ?? it.sold_quantity ?? it.qty ?? it.quantity) || 0;
  const lineWeight = (it) =>
    parseFloat(it.sold_weight ?? it.weight) || 0;
  const lineTotal = (it) => vendorItemAmount(it);
  const linePrice = (it) =>
    parseFloat(it.unit_price ?? it.single_price ?? it.price ?? it.product?.price) || 0;

  // If product rollup is empty, sum from sale lines (same fields as on-screen report).
  if (totalKg <= 0 && totalCases <= 0) {
    (s.sales || []).forEach((sale) => {
      (sale.items || []).forEach((it) => {
        totalCases += lineQty(it);
        totalKg += lineWeight(it);
      });
    });
  }

  const avgRate = totalKg > 0 ? gross / totalKg : (totalCases > 0 ? gross / totalCases : 0);

  let accountant = "";
  try {
    const u = JSON.parse(localStorage.getItem("sessionUser") || "{}");
    accountant = u?.name || u?.username || "";
  } catch { /* ignore */ }

  const fromStr = dateFrom || s.date_from || null;
  const toStr = dateTo || s.date_to || null;
  const period = (fromStr || toStr)
    ? tr("farmerPrintPeriodFromTo", "From: {{from}} To: {{to}}", {
      from: fromStr ? dayjs(fromStr).format("YYYY-MM-DD HH:mm") : "—",
      to: toStr ? dayjs(toStr).endOf("day").format("YYYY-MM-DD HH:mm") : "—",
    })
    : "";
  const printTime = settledAt
    ? dayjs(settledAt).format("h:mm:ss A M/D/YYYY")
    : dayjs().format("h:mm:ss A M/D/YYYY");
  const statementNo = documentNo
    || (archiveId ? formatSettlementNo(archiveId, settledAt) : formatLiveStatementNo(vendor.id, settledAt));

  const leftBox = (label, value, { html = false } = {}) => `
    <div class="lrow">
      <div class="lbl">${esc(label)}</div>
      <div class="box">${html ? value : esc(value)}</div>
    </div>`;
  const rightBox = (label, value, strong) => `
    <div class="rrow${strong ? " strong" : ""}">
      <div class="box">${esc(value)}</div>
      <div class="lbl">${esc(label)}</div>
    </div>`;

  const expenseBoxes = expenseRows
    .map((r) => rightBox(r.label, money(r.amount)))
    .join("");

  const walkIn = tr("posWalkIn", "Walk-in");
  const kgLabel = tr("kg", "kg");
  const piecesLabel = tr("qty", "Qty");
  const detailRows = [];
  (s.sales || []).forEach((sale) => {
    const items = Array.isArray(sale.items) && sale.items.length
      ? sale.items
      : [{
        product: null,
        sold_qty: null,
        qty: null,
        quantity: null,
        sold_weight: null,
        weight: null,
        unit_price: null,
        subtotal: sale.gross_sold ?? sale.gross_total,
        net: sale.net_owed,
      }];
    items.forEach((it) => {
      const pname = it.product
        ? parseLocalizedName(it.product.name, language, "—")
        : "—";
      const w = lineWeight(it);
      const q = lineQty(it);
      const qtyLine = q > 0 ? `${formatNumber(q)} ${esc(piecesLabel)}` : "—";
      const weightLine = w > 0 ? `${formatNumber(w)} ${esc(kgLabel)}` : "—";
      detailRows.push(`<tr>
        <td class="qw"><span>${qtyLine}</span><span>${weightLine}</span></td>
        <td>${money(linePrice(it))}</td>
        <td>${esc(pname)}</td>
        <td>${money(lineTotal(it))}</td>
        <td>${esc(sale.customer?.name || walkIn)}</td>
        <td>${sale.created_at ? dayjs(sale.created_at).format("MMM D, YYYY, h:mm A") : "—"}</td>
        <td>${esc(sale.cashier || accountant || "—")}</td>
      </tr>`);
    });
  });

  const logoHtml = logoSrc
    ? `<img class="logo" src="${esc(logoSrc)}" alt="" />`
    : `<div class="logo-ph">${esc(tr("farmerPrintLogo", "Logo"))}</div>`;

  const html = `<!doctype html><html dir="${rtl ? "rtl" : "ltr"}" lang="${esc(lang)}"><head><meta charset="utf-8">
<title>${esc(tr("farmerPrintDocTitle", "Farmer Statement"))}</title>
<style>
  ${PRINT_ADAPTIVE_CSS}
  @page { margin: 7mm; }
  * { box-sizing: border-box; }
  body {
    margin: 0; padding: 0;
    font-family: Tahoma, "Segoe UI", Arial, sans-serif;
    color: ${colors.primary}; font-size: 15px; font-weight: 700; direction: ${rtl ? "rtl" : "ltr"};
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }
  .sheet { width: 100%; max-width: 100%; margin: 0 auto; }
  .header {
    text-align: center; margin-bottom: 10px;
  }
  .logo, .logo-ph {
    width: 72px; height: 72px; object-fit: contain; display: block; margin: 0 auto 6px;
  }
  .logo-ph {
    border: 1px solid ${colors.primary}; background: ${colors.soft}; text-align: center; line-height: 72px;
    font-size: 13px; font-weight: 800; color: ${colors.primary}; opacity: 0.7; margin-left: auto; margin-right: auto;
  }
  .company { font-size: 22px; font-weight: 900; margin: 0; color: ${colors.accent}; }
  .tagline { font-size: 14px; font-weight: 800; margin: 4px 0 6px; }
  .managers {
    font-size: 13px; font-weight: 800; line-height: 1.45; margin: 0 0 4px; color: ${colors.primary};
  }
  .top-meta {
    display: flex; justify-content: space-between; align-items: flex-start;
    gap: 10px; margin-bottom: 8px; direction: ltr; font-size: 14px; font-weight: 800;
  }
  .farmer-id { color: ${colors.accent}; }
  .for-block { text-align: ${rtl ? "right" : "left"}; direction: ${rtl ? "rtl" : "ltr"}; color: ${colors.accent}; }
  .period {
    display: flex; justify-content: space-between; font-weight: 800;
    margin: 6px 0 10px; direction: ltr; font-size: 14px; color: ${colors.primary};
  }
  .mid {
    display: grid; grid-template-columns: 1fr 1fr; gap: 20px;
    direction: ltr; border-bottom: 2px solid ${colors.primary}; padding-bottom: 10px; margin-bottom: 10px;
  }
  .lrow, .rrow { display: grid; align-items: center; gap: 8px; margin-bottom: 6px; }
  .lrow { grid-template-columns: 120px 1fr; }
  .rrow { grid-template-columns: 1fr 140px; }
  .lbl { font-size: 14px; font-weight: 800; direction: ${rtl ? "rtl" : "ltr"}; text-align: ${rtl ? "right" : "left"}; color: ${colors.primary}; }
  .box {
    border: 1.4px solid ${colors.primary}; min-height: 28px; padding: 4px 8px;
    font-size: 15px; font-weight: 800; display: flex; flex-direction: column; justify-content: center; align-items: flex-start;
    font-family: "Courier New", Tahoma, monospace; direction: ltr; color: ${colors.accent}; gap: 2px;
  }
  .box .stack-line { display: block; line-height: 1.25; }
  .lrow .box { justify-content: center; align-items: flex-start; }
  .rrow .box { justify-content: center; align-items: flex-end; text-align: right; }
  .rrow.strong .box { border-width: 2px; font-weight: 900; font-size: 16px; border-color: ${colors.accent}; }
  .notes-block {
    border: 1.5px solid ${colors.primary}; padding: 8px 10px; margin: 0 0 10px; direction: ${rtl ? "rtl" : "ltr"};
  }
  .notes-block .notes-title { font-weight: 900; font-size: 15px; margin-bottom: 8px; color: ${colors.accent}; }
  .notes-block .note-debt {
    display: flex; justify-content: space-between; align-items: center; gap: 12px;
    direction: ${rtl ? "rtl" : "ltr"}; font-size: 14px; font-weight: 800; padding: 3px 0;
  }
  .notes-block .note-debt-sum {
    border-top: 1.5px solid ${colors.primary}; margin-top: 6px; padding-top: 8px; font-size: 16px; font-weight: 900;
  }
  .notes-block .note-debt .lbl { text-align: ${rtl ? "right" : "left"}; }
  .notes-block .note-debt .amt {
    font-family: "Courier New", Tahoma, monospace; direction: ltr; text-align: left; font-weight: 900; color: ${colors.accent};
  }
  /* Keep column order LTR always: Product … Name (Name on the right). */
  .detail {
    width: 100%; border-collapse: collapse; table-layout: fixed;
    direction: ltr;
  }
  .detail th, .detail td {
    border: 1.2px solid ${colors.primary}; padding: 3px 5px; text-align: center;
    font-size: 13px; font-weight: 800; vertical-align: middle; word-break: break-word;
    direction: ${rtl ? "rtl" : "ltr"};
  }
  .detail th { font-weight: 900; font-size: 12px; background: ${colors.soft}; color: ${colors.primary}; }
  .detail td { font-family: "Courier New", Tahoma, monospace; font-weight: 800; direction: ltr; }
  .detail .qw { line-height: 1.15; font-size: 12px; }
  .detail .qw span { display: block; }
  .empty { color: ${colors.primary}; opacity: 0.55; font-weight: 700; font-family: Tahoma, sans-serif; }
</style></head><body>
  <div class="sheet">
    <div class="header">
      ${logoHtml}
      ${company ? `<div class="company">${esc(company)}</div>` : ""}
      ${tagline ? `<div class="tagline">${esc(tagline)}</div>` : ""}
      ${managersHtml ? `<div class="managers">${managersHtml}</div>` : ""}
    </div>
    <div class="top-meta">
      <div class="farmer-id">${esc(statementNo)}</div>
      <div class="for-block">${esc(tr("farmerPrintFor", "For: {{name}}", { name: vendor.name || "—" }))}</div>
    </div>
    <div class="period">
      <span>${esc(period)}</span>
      <span>${esc(printTime)}</span>
    </div>
    <div class="mid">
      <div>
        ${leftBox(tr("farmerPrintName", "Farmer name"), vendor.name || "—")}
        ${leftBox(
          tr("qty", "Qty"),
          formatNumber(totalCases)
        )}
        ${leftBox(
          tr("weight", "Weight"),
          `${formatNumber(totalKg)} ${kgLabel}`
        )}
        ${leftBox(tr("farmerPrintAvg", "Average"), money(avgRate))}
        ${leftBox(tr("farmerPrintBalance", "Balance"), money(net))}
      </div>
      <div>
        ${rightBox(tr("vendorGross", "Gross"), money(gross))}
        ${expenseBoxes}
        ${rightBox(tr("farmerSlipTotalExpenses", "Total expenses"), money(totalExp))}
        ${rightBox(tr("vendorNet", "Net"), money(net), true)}
      </div>
    </div>
    <div class="notes-block">
      <div class="notes-title">${esc(tr("farmerSlipNotes", "Notes"))}</div>
      <div class="note-debt">
        <span class="lbl">${esc(tr("farmerPrintAccountant", "Accountant"))}</span>
        <span class="amt">${esc(printedBy?.name || "—")}</span>
        <span class="amt">${esc(printedBy?.phone || "—")}</span>
      </div>
    </div>
    <table class="detail">
      <thead>
        <tr>
          <th style="width:10%">${esc(tr("qty", "Qty"))} / ${esc(tr("weight", "Weight"))}</th>
          <th style="width:9%">${esc(tr("farmerPrintPrice", "Price"))}</th>
          <th style="width:16%">${esc(tr("farmerPrintProduct", "Product"))}</th>
          <th style="width:12%">${esc(tr("farmerPrintTotal", "Total"))}</th>
          <th style="width:14%">${esc(tr("farmerPrintCustomer", "Customer"))}</th>
          <th style="width:15%">${esc(tr("farmerPrintDateTime", "Date/time"))}</th>
          <th style="width:10%">${esc(tr("farmerPrintCashier", "Name"))}</th>
        </tr>
      </thead>
      <tbody>
        ${detailRows.length ? detailRows.join("") : `<tr><td colspan="7" class="empty">—</td></tr>`}
      </tbody>
    </table>
  </div>
  <script>
    (function () {
      var done = false;
      function closePage() {
        if (done) return;
        done = true;
        setTimeout(function () { try { window.close(); } catch (e) {} }, 350);
      }
      window.onafterprint = closePage;
      try {
        var mql = window.matchMedia("print");
        var onChange = function (e) { if (!e.matches) closePage(); };
        if (mql.addEventListener) mql.addEventListener("change", onChange);
        else if (mql.addListener) mql.addListener(onChange);
      } catch (e) {}
    })();
  </script>
</body></html>`;

  printHtml(html, printWin);
}
